/**
 * V2 product page behaviour (AB#528).
 *
 * Two jobs:
 *   1. wire the buy form, so an option selection resolves to a variant id
 *   2. make the tables inside a product DESCRIPTION scroll instead of overflowing the page,
 *      which has been outstanding since AB#521
 *
 * WHY THIS IS JS AND NOT CSS OR LIQUID.
 *
 * The descriptions are the merchant's HTML, and David's instruction is to keep them exactly as
 * written: nothing hidden, nothing stripped. So the table markup cannot be changed, and a bare
 * `<table>` cannot be made to scroll by styling the table alone. It needs a wrapper with
 * `overflow-x`, and the only place to add one without editing 28 descriptions is at runtime.
 *
 * `overflow-x: auto` on the description container itself was the obvious alternative and is
 * wrong: it would scroll the whole description, so a shopper scrolling a wide table would drag
 * the paragraphs sideways too.
 *
 * The wrapper is focusable with a role and a name, because a scroll container that only responds
 * to a pointer is unreachable by keyboard. Same rule the size guide's scroller follows.
 *
 * Idempotent: a table already inside a wrapper is left alone, so a re-render cannot nest
 * wrappers inside wrappers.
 */

import { wireVariantResolution, addToCart, setBusy } from './mlr-variant-form.js';

const WRAPPER_CLASS = 'mlr-rte-scroller';

/** Wrap every unwrapped table in `root` so it scrolls on its own. */
function wrapTables(root) {
  const tables = root.querySelectorAll('table');
  let wrapped = 0;

  for (const table of tables) {
    if (table.parentElement?.classList.contains(WRAPPER_CLASS)) continue;

    const wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    wrapper.tabIndex = 0;
    wrapper.setAttribute('role', 'region');
    // Named from a caption when the merchant wrote one, so the name describes THIS table rather
    // than being the same generic string on every one.
    const caption = table.querySelector('caption')?.textContent?.trim();
    wrapper.setAttribute('aria-label', caption ? `${caption}, scrolls sideways` : 'Table, scrolls sideways');

    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    wrapped += 1;
  }

  return wrapped;
}

/**
 * The buy form.
 *
 * Resolution comes from mlr-variant-form.js, the same module the quick-add sheet uses, so the
 * two surfaces cannot disagree about what a selection means. `mlr-option-select.js` handles
 * completeness and this handles what it cannot know: whether the resolved variant exists and is
 * in stock.
 *
 * On submit it adds by fetch and lets the cart drawer open itself from the `cart:refresh` event,
 * which is the behaviour approved for AB#527: the drawer listens for the same events Horizon's
 * drawer consumed. The default form POST is prevented so the shopper is not navigated away from
 * the product they are looking at.
 */
function wireBuyForm() {
  for (const root of document.querySelectorAll('[data-mlr-pdp]')) {
    if (root.dataset.mlrBuyWired === '1') continue;
    const wired = wireVariantResolution(root);
    if (!wired) continue;
    root.dataset.mlrBuyWired = '1';

    const { form, idInput, addButton, hint } = wired;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (addButton?.getAttribute('aria-disabled') === 'true') return;

      const id = idInput?.value;
      if (!id) {
        if (hint) hint.textContent = 'Choose an option to continue.';
        return;
      }

      setBusy(addButton, true);
      try {
        await addToCart(id, 1);
        // The cart drawer listens for this, the same event Horizon's drawer consumed.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (hint) hint.textContent = '';
      } catch (err) {
        if (hint) hint.textContent = err.message;
      } finally {
        setBusy(addButton, false);
      }
    });
  }
}

function init() {
  for (const root of document.querySelectorAll('[data-mlr-description]')) wrapTables(root);
  wireBuyForm();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Sections can re-render (the Section Rendering API), which replaces the description markup and
// loses the wrappers. Re-running on the event is cheaper than observing the whole subtree.
document.addEventListener('shopify:section:load', init);
